import React from "react";
import { useTranslation } from "react-i18next";
import { invalidate } from "@react-three/fiber";
import { observer } from "mobx-react-lite";
import { FiEye, FiEyeOff } from "react-icons/fi";
import { editorStore } from "../../../../store/editorStore";
import { floorplannerStore } from "../../../../store/floorplannerStore";
import {
  BackdropImageType,
  isBackdropImageType,
} from "../../../../types/floorplannerTypes";
import { isBackdropVisible } from "../../../../utils/backdropImage";
import PanelSection from "../components/PanelSection";

type BackgroundImagesSectionProps = {
  open: boolean;
  onToggle: () => void;
};

/**
 * Always-visible property-panel list of floorplan background images so they are
 * discoverable without opening Drawings & images. Thumbnails select the image,
 * toggle visibility, and show locked/unlocked/hidden status.
 */
const BackgroundImagesSection: React.FC<BackgroundImagesSectionProps> = observer(
  ({ open, onToggle }) => {
    const { t } = useTranslation();
    const backdrops = Array.from(floorplannerStore.symbolsMap.values()).filter(
      isBackdropImageType,
    );

    if (backdrops.length === 0) {
      return null;
    }

    const selectBackdrop = (backdrop: BackdropImageType) => {
      editorStore.clearSelections();
      floorplannerStore.updateSymbolProperty(backdrop.id, "selected", true);
      editorStore.addSelection(backdrop);
      invalidate();
    };

    const toggleBackdropVisibility = (
      event: React.MouseEvent,
      backdrop: BackdropImageType,
    ) => {
      event.stopPropagation();
      if (floorplannerStore.lockedByOther) return;
      floorplannerStore.beginUndoTransaction("backdropVisibility");
      floorplannerStore.recordSymbolForUndo(backdrop.id);
      floorplannerStore.updateSymbolProperty(
        backdrop.id,
        "visible",
        !isBackdropVisible(backdrop),
      );
      floorplannerStore.recordSymbolChange(backdrop.id, "modify");
      floorplannerStore.pushToUndoStack();
      floorplannerStore.setDirty();
      invalidate();
    };

    return (
      <PanelSection
        title={t("propertyPanel.backgroundImagesTitle")}
        collapsible
        open={open}
        onToggle={onToggle}
        maxHeightClassName="max-h-[480px]"
      >
        <div
          className="flex flex-col gap-2 px-3 pb-3"
          aria-label={t("propertyPanel.backgroundImagesTitle")}
        >
          <div className="grid grid-cols-1 gap-2">
            {backdrops.map((backdrop, index) => {
              const visible = isBackdropVisible(backdrop);
              const statusLabel = !visible
                ? t("common.hidden")
                : backdrop.locked
                  ? t("common.locked")
                  : t("common.unlocked");

              return (
                <button
                  key={backdrop.id}
                  type="button"
                  className={`group relative h-[140px] overflow-hidden rounded border bg-white text-left ${
                    backdrop.selected
                      ? "border-blue-500 ring-1 ring-blue-500"
                      : "border-[#edebe6]"
                  }`}
                  onClick={() => selectBackdrop(backdrop)}
                  title={t("uploads.openBackgroundSettings", {
                    index: index + 1,
                  })}
                >
                  <img
                    src={backdrop.backdropImagePath}
                    alt={t("uploads.backgroundIndex", { index: index + 1 })}
                    className={`h-full w-full object-cover ${
                      visible ? "" : "opacity-40"
                    }`}
                    crossOrigin="anonymous"
                  />
                  <div className="absolute inset-x-0 bottom-0 bg-black/55 px-2 py-1 text-[11px] text-white">
                    {statusLabel}
                  </div>
                  {!floorplannerStore.lockedByOther && (
                    <span
                      className="absolute left-1 top-1 flex h-7 w-7 items-center justify-center rounded bg-white text-gray-700 shadow hover:bg-gray-50"
                      onClick={(event) =>
                        toggleBackdropVisibility(event, backdrop)
                      }
                      title={
                        visible
                          ? t("uploads.hideBackgroundImage")
                          : t("uploads.showBackgroundImage")
                      }
                      aria-label={
                        visible
                          ? t("uploads.hideBackgroundImage")
                          : t("uploads.showBackgroundImage")
                      }
                    >
                      {visible ? <FiEye size={14} /> : <FiEyeOff size={14} />}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </PanelSection>
    );
  },
);

export default BackgroundImagesSection;
